import React from 'react';
import { Progress, ResponseErrorPanel } from '@backstage/core-components';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import useAsync from 'react-use/lib/useAsync';
import { useTriggerContext } from './TriggerContext';
import { TriggerTable } from './TriggerTable';
import { errorApiRef, useApi } from '@backstage/core-plugin-api';
import { REPONAME_ANNOTATION } from './config';
import { useEntity } from '@backstage/plugin-catalog-react';
import { RunTriggerOptions } from '@internal/backstage-plugin-cloudbuild-common';

export const Trigger: React.FC<{ entity: Entity }> = ({ entity }) => {
  const { api, projectNameResolver, locationResolver } = useTriggerContext();
  const { entity: catalogEntity } = useEntity();
  const repoName =
    catalogEntity.metadata.annotations?.[REPONAME_ANNOTATION] || '';

  const { value: projectName, loading: projectLoading } = useAsync(async () => {
    return projectNameResolver.getProjectName(entity);
  }, [entity]);

  const location = locationResolver.getLocation(entity) ?? '';
  const [projectId] = (projectName ?? '/').split('/');
  const [executing, setExecuting] = React.useState<string | null>(null);

  const errorApi = useApi(errorApiRef);

  const { value, loading, error } = useAsync(async () => {
    if (!projectId || !location) return [];

    return api.listTriggers({
      projectId,
      location,
      repoName,
      entityRef: stringifyEntityRef(entity),
    });
  }, [projectId, location]);

  const handleExecuteTrigger = async (
    triggerId: string,
    runTriggerOptions: RunTriggerOptions,
  ) => {
    setExecuting(triggerId);
    if (!projectId || !location) return;
    try {
      await api.runTrigger({
        ...runTriggerOptions,
        location,
      });
    } catch (err) {
      errorApi.post({
        name: 'Error',
        message: `Failed to run trigger | ${err}`,
      });
    } finally {
      setExecuting(null);
    }
  };

  if (loading || projectLoading) {
    return <Progress />;
  } else if (error) {
    return <ResponseErrorPanel error={error} />;
  }

  return (
    <TriggerTable
      triggers={value || []}
      onExecuteTrigger={handleExecuteTrigger}
      projectName={projectName}
      executingTrigger={executing}
    />
  );
};
